import { useArtifactReviewQueueIds } from "@/components/artifacts/artifactReviewQueue.ts";
import type { CanvasActionContextValue } from "@/components/canvas/canvasContext.tsx";
import { safeParseURN } from "@/types/urn.ts";
import { useEffect, useRef } from "react";

/**
 * Keep the canvas in step with the biscuit's carousel, so the arrows navigate
 * between artifacts on the canvas rather than leaving a stale card beside a
 * biscuit that has moved on.
 *
 * Only follows when the canvas is still presenting a card the carousel has left:
 * `getSnapshot` reports nothing while the panel is collapsed, and a drill-down
 * (or any unrelated reset) leaves a different stack top — neither should be
 * yanked to the new artifact.
 *
 * Covers every way the active artifact changes, not just the chevrons: arrow
 * keys, Tab, staging an action, and an artifact dropping out of the queue all
 * swap the prop this reads, and the canvas should follow all of them.
 *
 * Shared by every omnibox card ({@link ArtifactBiscuit},
 * {@link OmniArtifactFormBiscuit} and {@link OmniArtifactFormCard}) so they
 * can't drift on it — a card that skips it strands the canvas whenever the
 * carousel lands on that card.
 */
export function useCanvasFollowsCarousel(
  artifactUrn: string,
  canvas: CanvasActionContextValue | undefined,
  presentation: "canvas" | "inline" = "canvas",
): void {
  // The ids under review, which outlive any one card component. A form and a
  // heavy artifact are drawn by *different* components, so stepping between
  // them unmounts one card and mounts the other — and the incoming card's ref
  // starts life equal to the URN it just arrived at, leaving the step invisible
  // to it. That is how a paused tool call and the question paired with it ended
  // up in one review with arrows that moved the carousel and not the canvas.
  const queueIds = useArtifactReviewQueueIds();
  const presentedUrnRef = useRef(artifactUrn);
  const presented = canvas?.getSnapshot()?.canvasItem;
  useEffect(() => {
    const previousUrn = presentedUrnRef.current;
    presentedUrnRef.current = artifactUrn;
    if (!canvas || !presented) return;
    // Sign-in is complete in the bottom card. Retire a previously opened copy
    // rather than leave a second set of sign-in controls beside it.
    if (presented === artifactUrn) {
      if (presentation === "inline") {
        canvas.close();
        canvas.dropContent(presented);
      }
      return;
    }
    // Either this card component watched the carousel move, or the canvas is
    // sitting on another card of the same review — which only happens because
    // it moved. A queue is published for a live review of two or more cards,
    // so outside one this stays the per-card check it has always been.
    const presentedId = safeParseURN(presented)?.id;
    const leftBehind =
      presented === previousUrn || (presentedId !== undefined && queueIds.includes(presentedId));
    if (!leftBehind) return;
    if (presentation === "inline") {
      canvas.close();
      canvas.dropContent(presented);
    } else canvas.resetTo(artifactUrn);
  }, [artifactUrn, canvas, queueIds, presentation, presented]);
}
